import * as React from "react";
import { toast } from "react-toastify";
import { useQuery } from "@apollo/client";
import Card from "react-bootstrap/Card";
import apiClient from "api";
import { useNavigate } from "react-router-dom";
import BackToLink from "components/common/BackToLink";
import { Button, Form } from "react-bootstrap";
import { gettext } from "i18n";
import { useAuth, isAnyOrgAdmin, isOrgAdmin } from "state/auth";
import { getFormValueHandler } from "utils/forms";
import { GET_ORGANIZATIONS } from "utils/users";
import {
  GetOrganizations,
  GetOrganizations_organization,
} from "utils/__generated__/GetOrganizations";
import PageHeader from "theme/components/common/PageHeader";

const ReportingDashboardAdminAddPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [validated, setValidated] = React.useState<boolean>(false);
  const [submitDisabled, setSubmitDisabled] = React.useState<boolean>(false);
  const [editedValues, setEditedValues] = React.useState<Record<string, any>>(
    {},
  );
  const setValueHandler = getFormValueHandler(editedValues, setEditedValues);
  const { data } = useQuery<GetOrganizations>(GET_ORGANIZATIONS);
  const isGlobalAdmin = !!user?.admin;
  const anyOrgAdmin = isAnyOrgAdmin(user);

  const onSave = async (e: any) => {
    e.preventDefault();
    if (submitDisabled) {
      return;
    }
    setSubmitDisabled(true);
    const values = { ...editedValues };
    apiClient
      .request("/organization-admin/add-reporting-dashboard", {
        method: "POST",
        data: values,
      })
      .then(async () => {
        toast.success(gettext("Reporting dashboard created successfully"));
        setEditedValues({});
        setValidated(false);
        navigate(`/reporting-dashboards`);
        window.location.reload();
      })
      .catch((e) => {
        console.error(e);
        toast.error(
          `${gettext("Failed to create new Reporting Dashboard!")} ${gettext(
            e.toString(),
          )}`,
        );
      });
    setSubmitDisabled(false);
  };

  const handleSubmit = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    setValidated(true);

    const form = event.currentTarget;
    if (form.checkValidity()) {
      onSave(event);
    }
  };

  if (!isGlobalAdmin && !anyOrgAdmin) {
    return (
      <div>{gettext("You do not have permissions to access this page")}</div>
    );
  }

  const organizationList: GetOrganizations_organization[] | undefined =
    data?.organization;

  return (
    <div>
      <BackToLink to={`/reporting-dashboards`}>Back</BackToLink>
      <PageHeader
        title={gettext("Add new reporting dashboard")}
        className="mb-3"
      />
      <Card>
        <Card.Body>
          <Form noValidate validated={validated} onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>
                <strong>{gettext("Name")}</strong>
              </Form.Label>
              <Form.Control
                value={editedValues.name ?? ""}
                placeholder={gettext("Name")}
                onChange={setValueHandler("name")}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>
                <strong>{gettext("Organization")}</strong>
              </Form.Label>
              <Form.Select
                aria-label="Organization of the new reporting dashboard"
                onChange={setValueHandler("organization_id")}
                value={editedValues.organization_id ?? ""}
              >
                <option></option>
                {organizationList
                  ?.filter(
                    (organization: GetOrganizations_organization) =>
                      isGlobalAdmin || isOrgAdmin(user, organization.id),
                  )
                  .map((organization: GetOrganizations_organization) => (
                    <option
                      key={`org_${organization.id}`}
                      value={organization.id}
                    >
                      {organization.name}
                    </option>
                  ))}
              </Form.Select>
            </Form.Group>
            <Button type="submit" disabled={submitDisabled}>
              {gettext("Add")}
            </Button>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default ReportingDashboardAdminAddPage;
